import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signInWithEmailAndPassword, signInWithPopup, GoogleAuthProvider } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, firestore } from './firebase/config';

const BusinessUserSignIn = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Get user role from Firestore
      const userDoc = await getDoc(doc(firestore, 'users', user.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        
        // Check if user is actually a business user
        if (userData.role !== 'business_user') {
          setError('This account is registered as a job seeker account. Please use job seeker login.');
          return;
        }
        
        navigate('/business-dashboard');
      } else {
        setError('Account not found. Please sign up first.');
      }
    } catch (error) {
      console.error('Sign in error:', error);
      setError('Failed to sign in. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setLoading(true);

    try {
      const provider = new GoogleAuthProvider();
      const userCredential = await signInWithPopup(auth, provider);
      const user = userCredential.user;

      // Get user role from Firestore
      const userDoc = await getDoc(doc(firestore, 'users', user.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        
        // Check if user is actually a business user
        if (userData.role !== 'business_user') {
          setError('This account is registered as a job seeker account. Please use job seeker login.');
          return;
        }
        
        navigate('/business-dashboard');
      } else {
        setError('Account not found. Please sign up first.');
      }
    } catch (error) {
      console.error('Google sign in error:', error);
      setError('Failed to sign in with Google.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    navigate('/');
  };

  return (
    <div className="relative flex items-center justify-center min-h-screen w-full overflow-hidden bg-gradient-to-br from-orange-50 to-orange-100">
      {/* Background pattern */}
      <div className="absolute inset-0 grid grid-cols-4 grid-rows-3 gap-4 z-0">
        {Array(12).fill().map((_, i) => (
          <div key={i} className="rounded-lg bg-gradient-to-br from-orange-200 to-orange-100 opacity-50"></div>
        ))}
      </div>
      
      {/* Main container */}
      <div className="relative z-10 flex flex-row w-full max-w-4xl bg-white rounded-lg shadow-xl overflow-hidden">
        
        {/* Form side */}
        <div className="w-full md:w-1/2 p-8 md:p-12">
          <div className="flex items-center mb-8">
            <img 
              src="/asset/Logo/hireeLogo.png" 
              alt="Hiree Logo" 
              className="h-10 w-auto"
            />
            <span className="ml-3 text-xl text-gray-600">Business sign in</span>
          </div>
          
          {error && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <label htmlFor="email" className="block text-left text-gray-700">
                Email Address<span className="text-orange-500">*</span>
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your business email"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="password" className="block text-left text-gray-700">
                Password<span className="text-orange-500">*</span>
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400"
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900">
                  Remember me
                </label>
              </div>

              <div className="text-sm">
                <a href="#" className="font-medium text-orange-600 hover:text-orange-500">
                  Forgot your password?
                </a>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 px-4 bg-gradient-to-r from-orange-400 to-orange-500 text-white rounded-md hover:from-orange-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-opacity-50 disabled:opacity-70"
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </form>

          <div className="relative flex items-center justify-center my-4">
            <div className="border-t border-gray-300 w-full"></div>
            <span className="bg-white px-3 text-sm text-gray-500 absolute">or</span>
          </div>

          <button
            onClick={handleGoogleSignIn}
            disabled={loading}
            className="w-full py-3 border border-gray-300 rounded-md flex items-center justify-center space-x-2 hover:bg-gray-50 transition-colors disabled:opacity-70"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            <span>Continue with Google</span>
          </button>
          
          <div className="text-center mt-6">
            <p className="text-sm text-gray-600">
              Don't have an account?{' '}
              <Link to="/signup/business" className="text-orange-500 hover:text-orange-600">
                Sign up for business
              </Link>
            </p>
            <p className="text-sm text-gray-600 mt-2">
              <Link to="/login" className="text-gray-500 hover:text-gray-600">
                ← Back to login options
              </Link>
            </p>
          </div>
        </div>
        
        {/* Video side */}
        <div className="hidden md:block md:w-1/2 relative">
          <button 
            onClick={handleClose}
            className="absolute top-4 right-4 z-20 p-2 bg-white bg-opacity-70 rounded-full hover:bg-opacity-100"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <video autoPlay muted loop className="absolute inset-0 w-full h-full object-cover">
            <source src="/asset/SignInPage/video1.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </video>
          <div className="absolute inset-0 bg-black bg-opacity-30"></div>
          <div className="absolute bottom-8 left-8 z-20 text-white">
            <h2 className="text-2xl font-bold mb-2">Welcome Back</h2>
            <p className="text-sm opacity-90">Access your business dashboard</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessUserSignIn; 